import type { AiChatFragment } from "@src/features/assistantChat/aiChat.generated";
import { SelectedSession } from "@src/features/aiAssist/hooks/useChatSidebar";
import { CustomMessage } from "@src/features/assistantChat/ChatRuntimeProvider";
import { splitMessageIntoCanvasParts } from "@src/features/assistantChat/utils/canvas";
import { marked } from "marked";

/**
 * Backend now provides messages in almost the exact format for useChat hook
 * Only minor adjustments needed for type compatibility
 */
const transformChatExchangesToMessages = (
  session: AiChatFragment,
): CustomMessage[] => {
  const ret: CustomMessage[] = [];
  session.exchanges.forEach((exchange) => {
    const customMessageData: CustomMessage["customMessageData"] = {
      attachments: exchange.attachments.map((attachment) => ({
        libraryItemFragment: attachment.libraryItem,
        mediaFragment: attachment.media,
      })),
    };
    for (const vercelChatExchange of exchange.vercelChatMessages) {
      ret.push({
        id: vercelChatExchange.id,
        role: vercelChatExchange?.role,
        content: vercelChatExchange?.content,
        annotations: vercelChatExchange?.annotations || undefined,
        createdAt: vercelChatExchange.createdAt
          ? new Date(vercelChatExchange.createdAt)
          : undefined,
        customMessageData,
      } satisfies CustomMessage);
    }
  });
  return ret;
};

/**
 * Extracts the artifact from the most recent assistant message that contains canvas content
 */
const extractArtifactFromMessages = (
  messages: CustomMessage[],
): { artifact?: string; artifactMessageId?: string | null } => {
  // Find the most recent assistant message with canvas content
  for (let i = messages.length - 1; i >= 0; i--) {
    const message = messages[i];
    if (message.role === "assistant") {
      const messageParts = splitMessageIntoCanvasParts(message.content);
      if (messageParts.canvasPart && !messageParts.canvasIsStreaming) {
        // Convert markdown to HTML like in ThreadMessage.tsx
        const html = marked.parse(messageParts.canvasPart);
        if (typeof html === "string") {
          return {
            artifact: html,
            artifactMessageId: message.id,
          };
        }
      }
    }
  }
  return {};
};

/**
 * Creates a SelectedSession object from GraphQL AiSession data
 */
export function transformToSelectedSession(
  session: AiChatFragment,
): SelectedSession {
  const messages = transformChatExchangesToMessages(session);
  const { artifact, artifactMessageId } = extractArtifactFromMessages(messages);

  return {
    id: session.id,
    title: session.title,
    createdAt: session.createdAt,
    messages, // Keep as AI SDK Message[] format
    artifact,
    artifactMessageId,
  };
}
